import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Section, SectionHeader } from "@/components/Section";
import { CTASection } from "@/components/CTASection";
import { SITE } from "@/lib/site";
import { seoTitle, seoDescription } from "@/lib/seo-meta";
import { getService, getServiceLocationsBySlug } from "@/lib/services.functions";

const svcQ = (slug: string) => ({ queryKey: ["service", slug], queryFn: () => getService({ data: { slug } }), staleTime: 300_000 });
const locsQ = (slug: string) => ({ queryKey: ["service-locations", slug], queryFn: () => getServiceLocationsBySlug({ data: { service_slug: slug } }), staleTime: 300_000 });

export const Route = createFileRoute("/servicos/$slug")({
  loader: async ({ context, params }) => {
    const svc = await context.queryClient.ensureQueryData(svcQ(params.slug));
    if (!svc) throw notFound();
    await context.queryClient.ensureQueryData(locsQ(params.slug));
    return { svc };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Serviço não encontrado" }, { name: "robots", content: "noindex" }] };
    const svc = loaderData.svc as typeof loaderData.svc & { meta_title?: string | null; meta_description?: string | null; cover_image?: string | null };
    const t = svc.meta_title ?? seoTitle(`${svc.label} em Chapecó`);
    const d = svc.meta_description ?? seoDescription(svc.description || `${svc.label} profissional em Chapecó e região.`);
    const meta: Array<Record<string, string>> = [
      { title: t }, { name: "description", content: d },
      { property: "og:title", content: t }, { property: "og:description", content: d },
      { property: "og:url", content: `/servicos/${svc.slug}` },
      { property: "og:type", content: "article" },
    ];
    if (svc.cover_image) {
      meta.push({ property: "og:image", content: svc.cover_image });
      meta.push({ name: "twitter:image", content: svc.cover_image });
    }
    return {
      meta,
      links: [{ rel: "canonical", href: `${SITE.baseUrl}/servicos/${svc.slug}` }],
    };
  },
  component: ServiceDetail,
  errorComponent: ({ error }) => <div className="container-svg py-20 text-center">Erro: {String(error)}</div>,
  notFoundComponent: () => <div className="container-svg py-20 text-center"><h1 className="text-2xl font-bold">Serviço não encontrado</h1><Link to="/servicos" className="mt-4 inline-block btn-primary">Ver todos os serviços</Link></div>,
});

function ServiceDetail() {
  const { slug } = Route.useParams();
  const { data: svc } = useSuspenseQuery(svcQ(slug));
  const { data: locs = [] } = useSuspenseQuery(locsQ(slug));
  if (!svc) return null;
  const svcAny = svc as typeof svc & { cover_image?: string | null; long_description?: string | null };

  const cities = locs.filter((l) => !l.neighborhood);
  const bairros = locs.filter((l) => l.neighborhood);

  return (
    <>
      <section className="hero-bg">
        <div className="container-svg pt-12 pb-14 md:pt-20 md:pb-20">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] items-center">
            <div>
              <span className="eyebrow">Serviço · Chapecó/SC</span>
              <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl"><span className="text-gradient">{svc.label}</span> em Chapecó</h1>
              <p className="mt-5 max-w-2xl text-muted-foreground md:text-lg">{svcAny.long_description ?? svc.description}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href={SITE.whatsappUrl} target="_blank" rel="noopener" className="btn-accent px-6 py-3">Orçamento no WhatsApp</a>
                <Link to="/contato" className="btn-ghost px-6 py-3">Falar com a equipe</Link>
              </div>
            </div>
            {svcAny.cover_image ? (
              <div className="hidden lg:block">
                <img
                  src={svcAny.cover_image}
                  alt={`${svc.label} em Chapecó — Dedetizadora Chapecó`}
                  loading="eager"
                  width={1200}
                  height={800}
                  className="rounded-2xl shadow-[var(--shadow-glow)] ring-1 ring-border"
                />
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <Section>
        <SectionHeader eyebrow="Como funciona" title={`${svc.label} passo a passo`} />
        <ol className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {["Você entra em contato", "Fazemos a vistoria", "Enviamos o orçamento", "Executamos o serviço"].map((step, i) => (
            <li key={step} className="card-glass"><span className="text-primary font-bold text-2xl">{i + 1}</span><p className="mt-2 font-semibold">{step}</p></li>
          ))}
        </ol>
      </Section>

      <Section className="bg-[color:var(--color-sidebar)]">
        <SectionHeader eyebrow="onde atendemos" title={`${svc.label} nas cidades da região`} />
        <ul className="mt-8 grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 text-sm">
          {cities.map((l) => (
            <li key={l.slug}>
              <Link to="/$location/$service" params={{ location: l.location_slug!, service: slug }} className="block rounded border border-border bg-card px-3 py-2 hover:border-primary hover:text-primary transition-colors">{l.city} · SC</Link>
            </li>
          ))}
        </ul>
      </Section>

      <Section>
        <SectionHeader eyebrow="Chapecó" title={`${svc.label} por bairros de Chapecó`} />
        <ul className="mt-8 grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 text-sm">
          {bairros.map((l) => (
            <li key={l.slug}>
              <Link to="/$location/$service" params={{ location: l.location_slug!, service: slug }} className="block rounded border border-border bg-card px-3 py-2 hover:border-primary hover:text-primary transition-colors">{l.neighborhood}</Link>
            </li>
          ))}
        </ul>
      </Section>

      <CTASection title={`Precisa de ${svc.label.toLowerCase()} agora?`} text="Fale conosco pelo WhatsApp e receba orçamento gratuito." buttonLabel="Falar no WhatsApp" />
    </>
  );
}
